import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { DonationRow } from '../lib/donations';
import { Field } from './Field';
import { Select } from './Select';
import { Button } from './ui/Button';

// The choosable half of the model picker (arc 101 slice 11, plan doc 05) — the
// dropdown a donor's ceiling actually offers, plus the free-text field an open
// (`*`) ceiling adds. Split from `<DonationModelPicker/>` because this half owns
// STATE (what is selected, what was typed) and that one owns only the shape.
//
// Two rules it holds:
//
//   - **`freeText` is the open ceiling, and it is flagged unverified.** `*`
//     permits any model the donor's endpoint serves, and a `custom` endpoint's
//     models cannot be enumerated (probing the donor's key to fill a dropdown
//     would spend their money). So the maintainer types it, and is told plainly
//     that we cannot confirm the endpoint serves it.
//   - **A stored pick the catalog does not list reappears in the field it was
//     typed into.** Otherwise the next save would silently clear a model that is
//     running today.

/** The select value that means "clear the pick" — the donor's own default runs. */
const CLEAR = '';
/** The select value that reveals the free-text field. Never a model id. */
const CUSTOM = '__custom__';

export type DonationModelSelectProps = {
  readonly row: DonationRow;
  /** Exactly the models this gift may run — the ceiling, or the catalog under `*`. */
  readonly models: readonly string[];
  /** Whether a model outside {@link DonationModelSelectProps.models} may be typed. */
  readonly freeText: boolean;
  readonly offered: boolean;
  readonly busy: boolean;
  readonly onChoose: (model: string | null) => void;
};

export function DonationModelSelect(props: DonationModelSelectProps): JSX.Element {
  const stored = props.row.chosen_model;
  const typed = stored !== null && props.freeText && !props.models.includes(stored) ? stored : null;
  const [picked, setPicked] = createSignal(typed === null ? (stored ?? CLEAR) : CUSTOM);
  const [custom, setCustom] = createSignal(typed ?? '');

  const selectId = (): string => `donation-model-${props.row.id}`;
  const chosen = (): string | null => {
    if (picked() === CUSTOM) return custom().trim() === '' ? null : custom().trim();
    return picked() === CLEAR ? null : picked();
  };

  return (
    <div class="field-group">
      <Field
        label={t('donations.model_choose')}
        id={selectId()}
        desc={t(props.freeText ? 'donations.model_any_note' : 'donations.model_list_note')}
      >
        {(a11y) => (
          <>
            <Select
              a11y={a11y}
              label={t('donations.model_choose')}
              block
              value={picked()}
              disabled={!props.offered || props.busy}
              options={[
                { value: CLEAR, label: t('donations.model_default_option') },
                ...props.models.map((model) => ({ value: model, label: model })),
                ...(props.freeText
                  ? [{ value: CUSTOM, label: t('donations.model_custom_option') }]
                  : []),
              ]}
              onChange={setPicked}
            />

            <Show when={picked() === CUSTOM}>
              <input
                class="input"
                aria-label={t('donations.model_custom_label')}
                placeholder={t('donations.model_custom_placeholder')}
                value={custom()}
                disabled={props.busy}
                onInput={(e) => setCustom(e.currentTarget.value)}
              />
              <p class="field__desc">{t('donations.model_custom_unverified')}</p>
            </Show>
          </>
        )}
      </Field>

      <Show when={props.row.chosen_model === null}>
        <p class="field__desc">{t('donations.model_unpicked')}</p>
      </Show>

      <Show when={props.offered} fallback={<p class="field__desc">{t('donations.admin_only')}</p>}>
        <Button
          variant="outline"
          size="sm"
          disabled={props.busy}
          onClick={() => props.onChoose(chosen())}
        >
          {t(props.busy ? 'donations.model_saving' : 'donations.model_save')}
        </Button>
      </Show>
    </div>
  );
}
